import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import FirestoreRecoveryNotice from './shared/components/FirestoreRecoveryNotice';
import { reportFirestoreRuntimeFailure } from './shared/functions/firestoreRuntimeRecovery';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import {
  isChunkLoadError,
  recoverFromStaleCache,
  scheduleWaitingServiceWorkerUpdate,
} from './runtimeChunkRecovery';

// Tell the boot overlay (defined inline in public/index.html) that the JS
// bundle has parsed and React is about to mount. The overlay's auto-tick
// caps at ~90%; this jumps it forward so students see real progress.
try { window.__tsBoot && window.__tsBoot.set(92, 'Starting the app…'); } catch (e) {}


// The live tutorspace.app domain gets the friendly render-crash screen;
// localhost / preview deploys keep the full trace.
function isProductionHost() {
  try {
    const host = (window.location && window.location.hostname) || '';
    return /(^|\.)tutorspace\.app$/i.test(host);
  } catch (_) { return false; }
}
// A render crash on the live site replaces the whole app, so the boundary
// reloads once to try a clean mount. Guarded by sessionStorage so a
// deterministically-broken deploy can never loop — a second crash in the
// same session shows the visible ErrorBoundary fallback. Unhandled promise
// rejections and uncaught non-render errors never come here: they leave the
// page in place and are logged normally (see the window listeners below).
function recoverGenericProdError(reason) {
  if (!isProductionHost()) return false;
  const KEY = '__ts_generic_recovery_attempted__';
  try {
    if (sessionStorage.getItem(KEY)) return false; // never loop
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch (_) {}
  // eslint-disable-next-line no-console
  console.warn('[recovery] Render crash in prod — reloading once.', reason);
  setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 50);
  return true;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error), error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    if (isChunkLoadError(error)) {
      // Try the self-heal path; if it returns false we already attempted it
      // this session and should fall through to the visible fallback UI.
      recoverFromStaleCache(error);
    } else if (reportFirestoreRuntimeFailure(error)) {
      // React has already replaced the failing subtree. Keep recovery
      // explicit here too; never clear authentication or pending-write data.
    } else {
      // In prod (tutorspace.app) reload once after a render crash; in dev
      // fall through to the visible boundary so the developer can see the
      // trace.
      recoverGenericProdError(error);
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // While reload is in flight, show a soft message instead of a blank page.
        return (
          <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
            padding: 24, textAlign: 'center', color: '#374151'
          }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                Updating to the latest version…
              </div>
              <div style={{ fontSize: 14, opacity: 0.75, marginBottom: 16 }}>
                If this screen doesn't go away in a few seconds, please refresh the page.
              </div>
              <button
                type="button"
                onClick={() => recoverFromStaleCache(this.state.error, { force: true })}
                style={{
                  background: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Refresh now
              </button>
            </div>
          </div>
        );
      }
      const err = this.state.error;
      const errName = err?.name || (err && err.constructor && err.constructor.name) || 'Error';
      const errMessage = String(err?.message || err || 'Unknown error');
      const errStack = String(err?.stack || '');
      const compStack = String(this.state.errorInfo?.componentStack || '');

      // Friendly fallback for end users on the live site. Hides the
      // traceback and points them at support. Dev / preview hosts get
      // the verbose screen below so we can still debug.
      if (isProductionHost()) {
        return (
          <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
            padding: 24, textAlign: 'center', color: '#1f2937', background: '#f9fafb'
          }}>
            <div style={{ maxWidth: 480 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>😕</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, color: '#111827' }}>
                Something went wrong
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.5, opacity: 0.8, marginBottom: 20 }}>
                We hit an unexpected hiccup. Please try refreshing the page.
                If the problem keeps happening, reach out to{' '}
                <a
                  href="mailto:admin@tutorspace.app"
                  style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
                >
                  admin@tutorspace.app
                </a>{' '}
                and we'll get you sorted right away.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  Refresh
                </button>
                <a
                  href="mailto:admin@tutorspace.app"
                  style={{
                    background: '#fff', color: '#2563eb', border: '1px solid #d1d5db',
                    borderRadius: 8, padding: '10px 20px', fontWeight: 600,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center'
                  }}
                >
                  Contact support
                </a>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{
          minHeight: '100vh', padding: 24,
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          color: '#7f1d1d', background: '#fef2f2'
        }}>
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: '#dc2626', marginBottom: 6 }}>
              {errName.toUpperCase()}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'pre-wrap', marginBottom: 16 }}>
              {errMessage}
            </div>
            <pre style={{
              fontSize: 12, lineHeight: 1.4, background: '#fff', border: '1px solid #fecaca',
              borderRadius: 8, padding: 12, color: '#374151', maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap'
            }}>{errStack}</pre>
            {compStack ? (
              <pre style={{
                fontSize: 12, lineHeight: 1.4, background: '#fff', border: '1px solid #fecaca',
                borderRadius: 8, padding: 12, color: '#374151', marginTop: 12, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap'
              }}>Component stack:{compStack}</pre>
            ) : null}
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer' }}
              >Reload</button>
              <button
                type="button"
                onClick={() => this.setState({ hasError: false, isChunkError: false, error: null, errorInfo: null })}
                style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer' }}
              >Dismiss</button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Connection hiccups the Firestore SDK reports while its streams reconnect.
// 'failed-precondition' is deliberately NOT here: Firestore uses it for a
// missing composite index, which never heals by retrying and must stay visible.
function isFirestoreTransientError(reason) {
  if (!reason) return false;
  const msg = (reason && (reason.message || reason.toString())) || '';
  const code = reason && reason.code;
  // Surfacing these in the dev overlay just scares users for what is almost
  // always a brief WebChannel hiccup; they are still logged below.
  if (code === 'unavailable' || code === 'deadline-exceeded') return true;
  if (/client is offline/i.test(msg)) return true;
  if (/Failed to get document because the client is offline/i.test(msg)) return true;
  if (/WebChannelConnection RPC .* stream .* transport errored/i.test(msg)) return true;
  return false;
}

// Missing deploy chunks retain their bounded recovery. A fatal Firestore
// queue cannot be repaired by resubscribing; show an explicit refresh notice
// while leaving the page, authentication and persistent pending writes intact.
// Never infer cache corruption from an SDK value error or delete browser data.
// Denied writes, missing indexes and unrelated programming errors still reach
// their existing handlers and normal error reporting.
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    recoverFromStaleCache(event.reason);
  } else if (reportFirestoreRuntimeFailure(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  } else if (isFirestoreTransientError(event.reason)) {
    // Keep this connection hiccup out of CRA's dev error overlay; it is
    // still logged, and the calling surface owns any retry.
    event.preventDefault();
    event.stopImmediatePropagation();
    try { console.warn('[firestore] transient connection error:', event.reason && event.reason.message); } catch (e) {}
  }
}, true); // capture phase: run before CRA's dev error overlay listener
window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error || event)) {
    event.stopImmediatePropagation();
    recoverFromStaleCache(event.error || event.message);
  } else if (reportFirestoreRuntimeFailure(event.error || event.message || event)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  } else if (isFirestoreTransientError(event.error || event)) {
    // Same swallow path for sync throws of the offline error.
    event.preventDefault();
    event.stopImmediatePropagation();
    try { console.warn('[firestore] transient connection error:', (event.error && event.error.message) || event.message); } catch (e) {}
  }
}, true);

//App.use(cors(corsOptions));

// React 18 concurrent root: automatic batching of every state update
// (including promise/timeout-resolved ones), which legacy ReactDOM.render
// mode did not do — the single biggest render-count reduction available
// to the heavy counseling surfaces.
//
// createRoot has no render-complete callback (legacy arg 3), so the boot
// overlay's first-commit signal moves to an effect that fires after the
// first paint. Don't rely solely on `window.load` because a single stuck
// subresource (image / font / iframe / 3rd-party script) can delay it
// indefinitely and leave the overlay frozen at "Almost ready…" until the
// 4s safety timeout fires.
function BootOverlaySignal({ children }) {
  React.useEffect(() => {
    try { setTimeout(finishBoot, 0); } catch (e) {}
  }, []);
  return children;
}

createRoot(document.getElementById("root")).render(
  <>
    <FirestoreRecoveryNotice />
    <ErrorBoundary>
      <BootOverlaySignal>
        <App />
      </BootOverlaySignal>
    </ErrorBoundary>
  </>
);

// Once the full page (including lazy chunks fetched as part of initial
// render) has loaded, fade out the boot overlay defined in public/index.html.
// We also fire on `window.load` as a belt-and-suspenders backup in case the
// render-callback path is missed.
function finishBoot(){
  try {
    if (window.__tsBoot) {
      window.__tsBoot.set(100, 'Ready');
      setTimeout(function(){ try { window.__tsBoot && window.__tsBoot.done(); } catch(e){} }, 200);
    }
  } catch (e) {}
}
if (document.readyState === 'complete') {
  setTimeout(finishBoot, 80);
} else {
  window.addEventListener('load', function(){ setTimeout(finishBoot, 80); }, { once: true });
}

// Activate a waiting service worker so returning students pick up the new
// bundle quickly, but DEFER the page reload until the tab is hidden (user
// switched tabs / minimized) so we can never interrupt an in-flight signup
// (e.g. before the welcome email request finishes), login, or any form
// the user is actively filling in. If the user never makes the tab idle,
// the ErrorBoundary above will still self-heal the white screen on the
// next navigation that hits a stale chunk.
// In local dev, actively unregister any existing service worker. CRA's
// SW is built only for production, but if a developer ever loaded the
// production bundle on localhost, the SW persists and starts intercepting
// dev requests with stale precached chunk URLs — causing 404s, white
// screens, and `auth/network-request-failed` from Firebase. Skip
// registration entirely on localhost and clear any leftover SW.
const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname);
if (isLocalhost) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => { try { r.unregister(); } catch (e) {} });
    }).catch(() => {});
  }
} else {
  serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    scheduleWaitingServiceWorkerUpdate(registration);
  },
  });
}
